import {useLayoutEffect} from '@github-ui/use-layout-effect'
import {useCallback, useRef, useState} from 'react'

/**
 * useFlexWrapHysteresis
 * Detects if flex children are wrapped and applies hysteresis to avoid rapid toggling at breakpoints.
 * @param UNWRAP_BUFFER - px buffer before allowing unwrapping
 * @returns { ref, isWrapped }
 */
export function useFlexWrapHysteresis(UNWRAP_BUFFER = 40) {
  const flexRef = useRef<HTMLDivElement>(null)
  const [isWrapped, setIsWrapped] = useState(false)
  const [wrapThreshold, setWrapThreshold] = useState<number | null>(null)

  const checkWrap = useCallback(() => {
    const container = flexRef.current
    if (!container) return
    const children = Array.from(container.children)
    if (children.length < 2) {
      setIsWrapped(false)
      setWrapThreshold(null)
      return
    }
    const firstTop = (children[0] as HTMLElement).offsetTop
    const wrapped = children.some(child => (child as HTMLElement).offsetTop !== firstTop)

    setIsWrapped(prev => {
      if (prev !== wrapped) return wrapped
      return prev
    })

    if (wrapped) {
      if (!wrapThreshold || container.offsetWidth < wrapThreshold) setWrapThreshold(container.offsetWidth)
    } else {
      if (wrapThreshold && container.offsetWidth > wrapThreshold + UNWRAP_BUFFER) {
        setWrapThreshold(null)
      }
    }
  }, [wrapThreshold, UNWRAP_BUFFER])

  useLayoutEffect(() => {
    checkWrap()
    if (!flexRef.current) return
    const observer = new window.ResizeObserver(() => {
      checkWrap()
    })
    observer.observe(flexRef.current)
    return () => observer.disconnect()
  }, [checkWrap])

  return {flexRef, isWrapped}
}
